import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} PricePerKgRefs
 * @property {HTMLElement} priceContainer
 */

/**
 * A custom element that displays the price per kilogram of the selected variant.
 * The value itself is computed in Liquid (price / weight), so this component never
 * does arithmetic: on variant change it simply swaps in the server-rendered markup,
 * mirroring the behaviour of the native `product-price` component.
 *
 * @extends {Component<PricePerKgRefs>}
 */
class PricePerKg extends Component {
  /** @type {Element | null} */
  #closestSection = null;

  connectedCallback() {
    super.connectedCallback();
    this.#closestSection = this.closest('.shopify-section, dialog');
    if (!this.#closestSection) return;
    this.#closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (!this.#closestSection) return;
    this.#closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#closestSection = null;
  }

  /**
   * Handles product select event and swaps in the re-rendered price per kg.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer } = this.refs;

        // Find this same block inside the updated HTML
        const newPricePerKg = html.querySelector(`price-per-kg[data-block-id="${this.dataset.blockId}"]`);
        if (!newPricePerKg) return;

        const newContainer = newPricePerKg.querySelector('[ref="priceContainer"]');
        if (newContainer && priceContainer) {
          priceContainer.replaceWith(newContainer);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[price-per-kg] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('price-per-kg')) {
  customElements.define('price-per-kg', PricePerKg);
}
